import Button from 'ext-ui/components/Button/Button'
import AlertTriangleYellowIcon from 'ext-ui/svg/icon/AlertTriangleYellowIcon'
import ArrowIcon from 'ext-ui/svg/icon/ArrowIcon'
import React, { ReactElement } from 'react'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { useTranslate } from 'src/localization/Trns'
import { virtualCardsUrl } from 'src/navigation/urls'

import style from './CancelSubscriptionDowngrade.style'
import DeactivationNotice from './DeactivationNotice'
import DowngradeCardCount from './DowngradeCardCount'

// Starter plan caps active virtual cards at 100. We can't read this from the
// usages endpoint because it returns the *user's current plan's* entitlement
// (e.g. Pro = 500), so the Starter target is hardcoded here.
const STARTER_VIRTUAL_CARD_LIMIT = 100

interface CancelSubscriptionDowngradeProps {
  onConfirm: () => void
  onCancel: () => void
  billingCycleEnd: string | null
  billingCycleEndShort: string | null
  usage: number | undefined
}

export default function CancelSubscriptionDowngrade(
  props: CancelSubscriptionDowngradeProps,
): ReactElement {
  const { onConfirm, onCancel, billingCycleEnd, billingCycleEndShort, usage } =
    props
  const translate = useTranslate()
  const { analytics } = useAnalyticsContext()

  // Default to over-limit treatment until the usages query settles so an
  // over-cap user can't click past the gate during the loading window.
  const withinStarterLimit =
    typeof usage === 'number' ? usage <= STARTER_VIRTUAL_CARD_LIMIT : false
  const overBy =
    typeof usage === 'number'
      ? Math.max(0, usage - STARTER_VIRTUAL_CARD_LIMIT)
      : 0
  // The notice names an exact count only when usage is known AND over the
  // limit; otherwise it uses "excess virtual cards" (no misleading "0").
  const noticeWithinLimit = !(
    typeof usage === 'number' && usage > STARTER_VIRTUAL_CARD_LIMIT
  )

  const handleManageCardsClick = (): void => {
    analytics.track('cancel subscription', {
      action: 'manage_cards_clicked',
    })
  }

  return (
    <div css={style} data-testid="cancel-step-downgrade">
      <div className="CancelSubscriptionDowngrade-content">
        <div className="CancelSubscriptionDowngrade-text">
          <h2 className="CancelSubscriptionDowngrade-header">
            {translate('Downgrade to Starter')}
          </h2>
          <p className="CancelSubscriptionDowngrade-body-text">
            {typeof usage === 'number'
              ? billingCycleEnd
                ? translate(
                    'Starter plans support up to ${entitlement} active virtual cards. Your organization currently has ${usage} active virtual cards. To avoid automatic deactivation, reduce your active virtual card count to ${entitlement} or fewer before ${billingCycleEnd}.',
                    {
                      entitlement: String(STARTER_VIRTUAL_CARD_LIMIT),
                      usage: String(usage),
                      billingCycleEnd,
                    },
                  )
                : translate(
                    'Starter plans support up to ${entitlement} active virtual cards. Your organization currently has ${usage} active virtual cards. To avoid automatic deactivation, reduce your active virtual card count to ${entitlement} or fewer before the end of your billing cycle.',
                    {
                      entitlement: String(STARTER_VIRTUAL_CARD_LIMIT),
                      usage: String(usage),
                    },
                  )
              : translate(
                  'Starter plans support up to ${entitlement} active virtual cards.',
                  { entitlement: String(STARTER_VIRTUAL_CARD_LIMIT) },
                )}
          </p>
        </div>

        {typeof usage === 'number' && (
          <DowngradeCardCount
            usage={usage}
            overBy={overBy}
            withinLimit={withinStarterLimit}
            billingCycleEndShort={billingCycleEndShort}
          />
        )}

        {!withinStarterLimit && typeof usage === 'number' && (
          <a
            className="CancelSubscriptionDowngrade-manage-card"
            href={virtualCardsUrl()}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleManageCardsClick}
            data-testid="cancel-downgrade-manage-cards"
          >
            <span className="CancelSubscriptionDowngrade-manage-card-text">
              {translate(
                'Go to your virtual cards to deactivate the ones you no longer need.',
              )}
            </span>
            <span className="CancelSubscriptionDowngrade-manage-card-cta">
              {translate('Manage Cards')}
              <ArrowIcon direction="right" aria-hidden="true" />
            </span>
          </a>
        )}

        <div
          className="CancelSubscriptionDowngrade-warning"
          data-testid="cancel-downgrade-warning"
        >
          <AlertTriangleYellowIcon
            aria-hidden="true"
            className="CancelSubscriptionDowngrade-warning-icon"
          />
          <div className="CancelSubscriptionDowngrade-warning-text">
            <DeactivationNotice
              billingCycleEnd={billingCycleEnd}
              overBy={overBy}
              withinLimit={noticeWithinLimit}
            />
          </div>
        </div>
      </div>

      <div className="CancelSubscriptionDowngrade-buttons">
        <Button
          variant="text"
          onClick={onCancel}
          data-testid="cancel-downgrade-cancel"
        >
          {translate('Keep Pro')}
        </Button>
        <Button
          variant="primary"
          onClick={onConfirm}
          // Block advancement until we know the usage count (covers the
          // loading window and a /usages error returning no count).
          disabled={typeof usage !== 'number'}
          data-testid="cancel-downgrade-confirm"
        >
          {translate('Confirm Downgrade')}
        </Button>
      </div>
    </div>
  )
}
